import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useProducts } from '../context/ProductContext';

const Products = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { products, loading, error, getCategories } = useProducts();

  const categories = getCategories();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-yellow-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20 flex items-center justify-center">
        <p className="text-red-400 text-xl">Error loading products. Please try again later.</p>
      </div>
    );
  }

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-purple-950 py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Our Ritual Oils</h1>
          <p className="text-xl text-purple-200">Hand-blended intention oils for your spiritual journey</p>
        </div>

        {/* Search and Filter */}
        <div className="mb-12">
          <div className="max-w-2xl mx-auto mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-400" />
              <Input
                type="text"
                placeholder="Search for ritual oils..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-purple-800/50 border-purple-600 text-white placeholder:text-purple-400"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map(category => (
              <Button
                key={category}
                onClick={() => setSelectedCategory(category)}
                variant={selectedCategory === category ? 'default' : 'outline'}
                className={selectedCategory === category 
                  ? 'bg-yellow-500 hover:bg-yellow-600 text-purple-900' 
                  : 'border-purple-600 text-purple-200 hover:bg-purple-800'}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {filteredProducts.map(product => (
            <Link key={product.id} to={`/product/${product.id}`}>
              <Card className="bg-purple-800/40 border-purple-600 hover:border-yellow-500 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-yellow-500/20 overflow-hidden group h-full">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4 bg-yellow-500 text-purple-900 px-3 py-1 rounded-full text-sm font-semibold">
                    ${product.basePrice ? product.basePrice.toFixed(2) : product.price?.toFixed(2)}
                  </div>
                </div>
                <CardContent className="pt-6">
                  <p className="text-purple-300 text-sm mb-2">{product.category}</p>
                  <h3 className="text-xl font-semibold mb-2 text-white">{product.name}</h3>
                  <p className="text-purple-200 text-sm line-clamp-3">{product.description}</p>
                  {product.uses && product.uses.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {product.uses.slice(0, 3).map((use) => (
                        <span
                          key={`products-use-${product.id}-${use}`}
                          className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-300 border border-yellow-500/30"
                          data-testid={`product-use-badge-${product.id}`}
                        >
                          {use}
                        </span>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-2xl text-purple-300">No products found matching your criteria.</p>
          </div>
        )}

        {/* Healing Frequencies Legend */}
        <div className="mt-24 max-w-4xl mx-auto" data-testid="frequencies-legend">
          <div className="relative rounded-2xl border border-yellow-400/30 bg-gradient-to-br from-purple-900/60 via-purple-800/40 to-purple-900/60 p-8 md:p-10 overflow-hidden">
            <div className="absolute -top-8 -right-8 w-48 h-48 bg-yellow-400/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="h-px w-10 bg-yellow-400/50" />
                <span className="text-yellow-300 text-xs tracking-[0.3em] uppercase">Solfeggio Sound Healing</span>
                <span className="h-px w-10 bg-yellow-400/50" />
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-white text-center mb-3">
                Each Oil Carries Its Own Frequency
              </h3>
              <p className="text-purple-200 text-center max-w-2xl mx-auto mb-8 text-sm md:text-base leading-relaxed">
                Every blend is tuned to a sacred solfeggio frequency chosen for its energetic
                purpose. Tap the <span className="text-yellow-300 font-medium">Play Healing Music</span>{' '}
                button on any product page to experience its unique vibration.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { hz: '396 Hz', meaning: 'Release', desc: 'Liberation from fear & guilt' },
                  { hz: '432 Hz', meaning: 'Calm', desc: 'Natural grounding & rest' },
                  { hz: '528 Hz', meaning: 'Love', desc: 'Healing & transformation' },
                  { hz: '639 Hz', meaning: 'Harmony', desc: 'Heart chakra & relationships' },
                  { hz: '741 Hz', meaning: 'Awakening', desc: 'Intuition & expression' },
                  { hz: '963 Hz', meaning: 'Divine', desc: 'Crown chakra & oneness' },
                ].map((f) => (
                  <div
                    key={`freq-${f.hz}`}
                    className="p-4 rounded-xl bg-purple-900/50 border border-purple-700/50 hover:border-yellow-400/40 hover:bg-purple-900/70 transition-all"
                    data-testid={`freq-card-${f.hz.replace(/\s+/g, '')}`}
                  >
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-yellow-300 font-bold text-xl">{f.hz}</span>
                      <span className="text-yellow-200/80 text-sm tracking-wide">· {f.meaning}</span>
                    </div>
                    <p className="text-purple-300 text-xs leading-relaxed">{f.desc}</p>
                  </div>
                ))}
              </div>

              <p className="text-center text-purple-400 text-xs italic mt-6">
                ✦ Sound frequencies complement — never replace — medical or therapeutic care ✦
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
